import { omit } from 'lodash';
import { utils as coreUtils } from '@wsb/guac-widget-core';
import { isLightColor } from '../../utils/colorUtils';
import { getColorModeCssVariables } from '../../utils/colorMode';

const COLOR_MODE_CSS_VAR_NAMES = coreUtils.COLOR_MODE_CSS_VAR_NAMES || {};
const COLOR_MODE_BG_VAR = COLOR_MODE_CSS_VAR_NAMES.body?.bg || '--wsb-bg';
const COLOR_MODE_TEXT_VAR = COLOR_MODE_CSS_VAR_NAMES.typography?.body || '--wsb-text';

export { getColorModeCssVariables };

export function resolveColorFromDialsMeta(theme, token) {
  const colorDials = (theme.base && theme.base.colorDials) || [];
  for (let index = 0; index < colorDials.length; index += 1) {
    const dial = colorDials[index];
    const value =
      (dial && typeof dial.getMeta === 'function' && dial.getMeta(token)) ||
      (dial && dial.meta && dial.meta[token]);
    if (value) {
      return value;
    }
  }
  return null;
}

export function resolveColorToHex(theme, token) {
  let value = null;
  try {
    if (theme.base && theme.base.theme && typeof theme.base.theme.mapPropValue === 'function') {
      value = theme.base.theme.mapPropValue('backgroundColor', token);
    }
  } catch (e) {
    /* swallow */
  }
  if (!value) {
    value = resolveColorFromDialsMeta(theme, token);
  }
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value.toHex === 'function') return value.toHex();
  if (typeof value.toString === 'function') return value.toString();
  return null;
}

export function resolveColorModeCssVariable(theme, cssVarName, colorModeOverride) {
  const vars = cssVarName && getColorModeCssVariables(theme, colorModeOverride);
  return vars ? vars[cssVarName] : null;
}

export function colorModeCssReference(theme, cssVarName, fallback, colorModeOverride) {
  return resolveColorModeCssVariable(theme, cssVarName, colorModeOverride)
    ? `!var(${cssVarName})`
    : fallback;
}

export function colorModeTextColor(theme, fallback) {
  return colorModeCssReference(theme, COLOR_MODE_TEXT_VAR, fallback);
}

export function resolveSectionBackgroundColor(theme) {
  return (
    resolveColorModeCssVariable(theme, COLOR_MODE_BG_VAR) || resolveColorToHex(theme, 'section')
  );
}

export function isLightSection(theme, colorModeOverride) {
  // When a color mode is EXPLICITLY set (color-mode section, or the spotlight whose media
  // overlay is painted from the same mode bg), resolve the mode bg FIRST so glass buttons
  // pick the right light/dark variant even when the surface context is `overlay`.
  // Guard on an explicit mode: getColorModeCssVariables falls back to `light1`, so without
  // this an opaque solid NAV (no mode, `section` reflects the nav's own background) would
  // wrongly read as light instead of contrasting the nav surface it actually sits on.
  const explicitColorMode = colorModeOverride || (theme.base && theme.base.colorMode);
  if (explicitColorMode) {
    const colorModeBg = resolveColorModeCssVariable(theme, COLOR_MODE_BG_VAR, explicitColorMode);
    if (colorModeBg) return isLightColor(colorModeBg, 65);
  }
  // No explicit mode: media/overlay surfaces are dark; otherwise use the section token.
  if (theme.base && theme.base.section === 'overlay') return false;
  const sectionHex = resolveColorToHex(theme, 'section');
  return sectionHex ? isLightColor(sectionHex, 65) : true;
}

export function getThemeOverrideValue(theme, overrideName) {
  const { websiteThemeOverrides = {}, widgetThemeOverrides = {} } = (theme && theme.base) || {};
  return theme.merge(
    {},
    websiteThemeOverrides[overrideName] && websiteThemeOverrides[overrideName].value,
    widgetThemeOverrides[overrideName] && widgetThemeOverrides[overrideName].value
  );
}

const BUTTON_STYLE_PROP_NAMES = [
  'shape',
  'fill',
  'decoration',
  'shadow',
  'color',
  'size',
  'treatment',
  'customBorderWidth'
];

const BUTTON_VISUAL_STYLE_PROP_NAMES = [
  'background',
  'backgroundColor',
  'backgroundImage',
  'backdropFilter',
  'WebkitBackdropFilter',
  'border',
  'borderColor',
  'borderStyle',
  'borderWidth',
  'boxShadow',
  'color',
  'textShadow',
  'overflow',
  'transition',
  'transform',
  ':hover',
  ':active'
];

export function omitButtonStyleProps(props = {}) {
  const passthroughProps = omit(props, [...BUTTON_STYLE_PROP_NAMES, 'style']);
  const passthroughStyle = omit(props.style || {}, BUTTON_VISUAL_STYLE_PROP_NAMES);
  return {
    ...passthroughProps,
    ...(Object.keys(passthroughStyle).length ? { style: passthroughStyle } : {})
  };
}
